import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Form,
  InputGroup,
  Pagination,
  Table,
} from "react-bootstrap";
import { FaEdit, FaEye, FaPlus, FaSearch, FaTimes, FaTrash, FaUndo } from "react-icons/fa";
import { toast } from "react-toastify";
import { catalogApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { buildPageItems } from "../utils/adminOrders";
import {
  PRODUCT_PAGE_SIZES,
  PRODUCT_SORT_OPTIONS,
} from "../utils/adminProducts";
import { TableSkeleton } from "../components/Skeleton";
import "./dashboard/ManagePages.css";

const DEFAULT_FILTERS = {
  search: "",
  status: "active",
  featured: "all",
  sortBy: "newest",
};

const AdminProducts = () => {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    if (!isAuthenticated || user?.role !== "admin") return;
    setLoading(true);
    try {
      const res = await catalogApi.adminProducts({
        page,
        limit: pageSize,
        search: filters.search || undefined,
        status: filters.status,
        featured: filters.featured,
        sort: filters.sortBy,
      });
      setProducts(res.data.data.products || []);
      setMeta(res.data.meta || { page, limit: pageSize, total: 0, pages: 1 });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load products"));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, page, pageSize, filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) return prev;
        setPage(1);
        return { ...prev, search: searchInput };
      });
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const hasActiveFilters =
    filters.search.trim() ||
    filters.status !== "active" ||
    filters.featured !== "all" ||
    filters.sortBy !== "newest";

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput("");
    setPage(1);
  };

  const handleDelete = async (product) => {
    const isInactive = product.isActive === false;
    const confirmed = window.confirm(
      isInactive
        ? `Permanently delete "${product.name}"?\n\nThis cannot be undone. The product and its reviews will be removed forever.`
        : `Deactivate "${product.name}"?\n\nThis will hide it from the shop and remove its images from the server if they are not used elsewhere. You can restore or permanently delete it later from Inactive.`
    );
    if (!confirmed) return;

    setDeletingId(product.id);
    try {
      await catalogApi.deleteProduct(product.id);
      await load();
      toast.success(
        isInactive
          ? "Product permanently deleted"
          : "Product deactivated and images cleaned up"
      );
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not delete product"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestore = async (product) => {
    const confirmed = window.confirm(
      `Restore "${product.name}" to active?\n\nIt will show in the shop again.`
    );
    if (!confirmed) return;

    setRestoringId(product.id);
    try {
      await catalogApi.restoreProduct(product.id);
      await load();
      toast.success("Product restored to active");
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not restore product"));
    } finally {
      setRestoringId(null);
    }
  };

  const totalPages = meta.pages || 1;
  const showingFrom = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const showingTo = Math.min(meta.page * meta.limit, meta.total);
  const pageItems = useMemo(
    () => buildPageItems(meta.page, totalPages),
    [meta.page, totalPages]
  );

  if (authLoading) return <TableSkeleton rows={6} cols={8} />;
  if (!isAuthenticated || user?.role !== "admin") {
    return <Navigate to="/login" state={{ from: "/dashboard/products" }} replace />;
  }

  return (
    <div className="manage-page">
      <header className="manage-head">
        <div>
          <h1>All products</h1>
          <p>Search, filter, and manage catalog items, variants, and visibility.</p>
        </div>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <span className="manage-count">{meta.total} total</span>
          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => navigate("/dashboard/categories")}
          >
            Categories
          </Button>
          <Button
            variant="dark"
            size="sm"
            onClick={() => navigate("/dashboard/products/new")}
          >
            <FaPlus /> Add product
          </Button>
        </div>
      </header>

      <div className="manage-toolbar">
        <InputGroup className="manage-search">
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search name, SKU, brand, tags…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </InputGroup>

        <Form.Select
          className="manage-filter"
          value={filters.status}
          onChange={(e) => updateFilter("status", e.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Form.Select>

        <Form.Select
          className="manage-filter"
          value={filters.featured}
          onChange={(e) => updateFilter("featured", e.target.value)}
        >
          <option value="all">Featured: all</option>
          <option value="yes">Featured only</option>
          <option value="no">Not featured</option>
        </Form.Select>

        <Form.Select
          className="manage-filter"
          value={filters.sortBy}
          onChange={(e) => updateFilter("sortBy", e.target.value)}
        >
          {PRODUCT_SORT_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Form.Select>

        <Form.Select
          className="manage-filter manage-page-size"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
        >
          {PRODUCT_PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </Form.Select>

        {hasActiveFilters && (
          <Button
            variant="outline-secondary"
            size="sm"
            className="manage-clear-btn"
            onClick={clearFilters}
          >
            <FaTimes /> Clear
          </Button>
        )}
      </div>

      {loading ? (
        <TableSkeleton rows={8} cols={8} />
      ) : meta.total === 0 && !hasActiveFilters ? (
        <div className="manage-empty">
          <p>No products yet.</p>
          <Button variant="dark" onClick={() => navigate("/dashboard/products/new")}>
            <FaPlus /> Add product
          </Button>
        </div>
      ) : products.length === 0 ? (
        <div className="manage-empty">
          <p>No products match your search or filters.</p>
          <Button variant="link" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          <div className="manage-card table-responsive">
            <Table hover className="manage-table manage-table-products align-middle mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="d-none d-md-table-cell">SKU</th>
                  <th className="d-none d-lg-table-cell">Category</th>
                  <th>Price</th>
                  <th className="d-none d-sm-table-cell">Stock</th>
                  <th className="d-none d-xl-table-cell">Variants</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.name}</strong>
                      {p.isFeatured && (
                        <div>
                          <Badge bg="warning" text="dark">
                            Featured
                          </Badge>
                        </div>
                      )}
                      <div className="manage-muted d-md-none">{p.sku || "—"}</div>
                    </td>
                    <td className="d-none d-md-table-cell">{p.sku || "—"}</td>
                    <td className="d-none d-lg-table-cell">
                      {p.category?.name || "—"}
                    </td>
                    <td>AED {Number(p.price).toFixed(2)}</td>
                    <td className="d-none d-sm-table-cell">{p.stock}</td>
                    <td className="d-none d-xl-table-cell">
                      {p.variantCount ?? p.variants?.length ?? 0}
                    </td>
                    <td>
                      {p.isActive !== false ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="secondary">Inactive</Badge>
                      )}
                    </td>
                    <td className="text-nowrap manage-row-actions">
                      <Link
                        to={`/product/${p.slug}`}
                        className="btn btn-sm btn-outline-secondary"
                        title="View"
                      >
                        <FaEye />
                      </Link>
                      <Link
                        to={`/dashboard/products/${p.id}/edit`}
                        className="btn btn-sm btn-outline-primary"
                        title="Edit"
                      >
                        <FaEdit />
                      </Link>
                      {p.isActive === false ? (
                        <Button
                          variant="outline-success"
                          size="sm"
                          title="Make active"
                          disabled={restoringId === p.id || deletingId === p.id}
                          onClick={() => handleRestore(p)}
                        >
                          <FaUndo />
                        </Button>
                      ) : null}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        title={
                          p.isActive === false
                            ? "Permanently delete"
                            : "Deactivate"
                        }
                        disabled={deletingId === p.id || restoringId === p.id}
                        onClick={() => handleDelete(p)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="manage-pagination-bar">
            <p className="manage-pagination-meta">
              Showing {showingFrom}–{showingTo} of {meta.total} products
            </p>
            {totalPages > 1 && (
              <Pagination className="manage-pagination mb-0">
                <Pagination.Prev
                  disabled={meta.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                />
                {pageItems.map((item, index) =>
                  item === "…" ? (
                    <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={item}
                      active={item === meta.page}
                      onClick={() => setPage(item)}
                    >
                      {item}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  disabled={meta.page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                />
              </Pagination>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminProducts;
